/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.92, "tu3": 3, "notes": "Global entity viewer stack store", "tuVersion": "2.5"}
 * .help "Zustand store driving the global EntityViewerHost. Call viewEntity(type, id) from anywhere to open the matching orchestrator modal in place. Stack-based so nested viewing (project -> sprint) returns to the previous viewer on close."
 */
// src/store/entityViewerStore.ts
// July 2026: Part of the Entity Viewer Model (docs/ENTITY_VIEWER_MODEL.md).
// Centralizes "view X" so pages don't hand-roll orchestrator modal state.
import { create } from 'zustand';

export type ViewableEntityType =
  | 'card'
  | 'sprint'
  | 'roadmap'
  | 'project'
  | 'feature'
  | 'plan';

export interface EntityViewerEntry {
  /** Unique key for React rendering (same entity can be pushed twice). */
  key: number;
  type: ViewableEntityType;
  id: string;
}

interface EntityViewerState {
  stack: EntityViewerEntry[];
  /** Open an entity in the global viewer, stacking on top of any open viewer. */
  viewEntity: (type: ViewableEntityType, id: string) => void;
  /** Close the top-most viewer (returns to the one below, if any). */
  closeTop: () => void;
  /** Close all viewers (e.g. on route change if ever needed). */
  closeAll: () => void;
}

let nextKey = 1;

const useEntityViewerStore = create<EntityViewerState>((set) => ({
  stack: [],

  viewEntity: (type, id) =>
    set((state) => {
      const top = state.stack[state.stack.length - 1];
      // No-op if the same entity is already on top (double-clicks, repeat focus)
      if (top && top.type === type && top.id === id) return state;
      return { stack: [...state.stack, { key: nextKey++, type, id }] };
    }),

  closeTop: () => set((state) => ({ stack: state.stack.slice(0, -1) })),

  closeAll: () => set({ stack: [] }),
}));

export default useEntityViewerStore;
